import type { IOmit } from '@sixui/ui';
import type { SubmitHandler } from 'react-hook-form';
import type { MapRef } from 'react-map-gl';
import { useCallback, useMemo, useRef, useState } from 'react';
import { faBell, faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createSequence } from '@olivierpascal/helpers';
import { Button, Checkbox, InputChip, Skeleton, Stack, Text } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { outlineTokens } from '@sixui/ui/themes/base/outline.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';
import distance from '@turf/distance';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { IBadgesFormValues } from '~/components/forms/BadgesForm';
import type { ISearchCommunityFormBodyValues } from '~/components/forms/SearchCommunityForm/SearchCommunityFormBody';
import type { ISendPushNotificationFormValues } from '~/components/forms/SendPushNotificationForm';
import type { IContainerProps } from '~/components/utils/Container';
import type { IGeolocation } from '~/helpers/types';
import type {
  IApiCrowdSearchFiltersRequestBody,
  IApiCrowdSearchItemResponseBody,
  IApiCrowdSearchRequestBody,
} from '~/lib/api/modules/Crowd';
import { BadgesFormDialog } from '~/components/forms/BadgesForm';
import { SendPushNotificationFormDialog } from '~/components/forms/SendPushNotificationForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Pagination } from '~/components/molecules/Pagination';
import {
  SearchCommunityListItem,
  SearchCommunityListItemSkeleton,
} from '~/components/molecules/SearchCommunityListItem';
import { Table } from '~/components/molecules/Table';
import { usePagination } from '~/hooks/usePagination';
import { useAddBadgesCrowdMutation } from '~/lib/api/modules/Crowd/hooks/useAddBadgesCrowdMutation';
import { useNotifyCrowdMutation } from '~/lib/api/modules/Crowd/hooks/useNotifyCrowdMutation';
import { useRemoveBadgesCrowdMutation } from '~/lib/api/modules/Crowd/hooks/useRemoveBadgesCrowdMutation';
import {
  searchCrowdQueryKeyFactory,
  useSearchCrowdQuery,
} from '~/lib/api/modules/Crowd/hooks/useSearchCrowdQuery';
import { SearchCommunityMap } from '../SearchCommunityMap';
import { SearchCommunityAside } from './SearchCommunityAside';

import 'mapbox-gl/dist/mapbox-gl.css';

import { useQueryClient } from 'react-query';

const MAP_ENABLED = true;
const GRID_TEMPLATE_COLUMNS =
  'min-content max-content min-content repeat(4, 1fr)';
const DEFAULT_SKELETON_COUNT = 10;

export type ISearchCommunityProps = IContainerProps & {
  appId: string;
};

const unserializeType = (type?: string): IApiCrowdSearchRequestBody['type'] => {
  switch (type) {
    case 'users':
      return ['user'];
    case 'devices':
      return ['device'];
    default:
      return ['user', 'device'];
  }
};

const styles = stylex.create({
  map: {
    display: 'flex',
    flexGrow: 1,
    width: '100%',
    height: 480,
    borderRadius: shapeTokens.corner$md,
    borderWidth: outlineTokens.width$xs,
    borderStyle: 'solid',
    borderColor: colorSchemeTokens.outlineVariant,
  },
});

type ISearchCommunityFilters = IOmit<
  IApiCrowdSearchFiltersRequestBody,
  'type'
> & {
  categoryId?: string;
  type?: string;
};

export const SearchCommunity: React.FC<ISearchCommunityProps> = (props) => {
  const { sx, appId } = props;
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<ISearchCommunityFilters>({
    search: searchParams.get('search') ?? '',
    categoryId: searchParams.get('categoryId') ?? undefined,
    articleId: searchParams.get('articleId') ?? undefined,
    onlyBadges: searchParams.get('onlyBadges') ?? undefined,
    badgeId: searchParams.get('badgeId') ?? undefined,
    sortBy:
      (searchParams.get('sortBy') as
        | IApiCrowdSearchRequestBody['sortBy']
        | null
        | undefined) ?? 'lastMetricAt',
    sortOrder:
      (searchParams.get(
        'sortOrder',
      ) as IApiCrowdSearchRequestBody['sortOrder']) ?? 'desc',
    type: searchParams.get('type') ?? 'usersAndDevices',
    lat: parseFloat(searchParams.get('lat') || '') || undefined,
    lng: parseFloat(searchParams.get('lng') || '') || undefined,
    radius: parseFloat(searchParams.get('radius') || '') || undefined,
  });
  const pagination = usePagination({
    extraParams: filters,
  });
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const searchCrowdQuery = useSearchCrowdQuery(
    {
      appId,
      body: {
        ...filters,
        type: unserializeType(filters.type),
        skip: pagination.start,
        limit: pagination.limit,
      },
    },
    {
      enabled: pagination.limit > 0,
      cacheTime: 0,
    },
  );
  const notifyCrowdMutation = useNotifyCrowdMutation();
  const addBadgesCrowdMutation = useAddBadgesCrowdMutation();
  const removeBadgesCrowdMutation = useRemoveBadgesCrowdMutation();
  const [selectedMembers, setSelectedMembers] = useState<
    Set<string> | undefined
  >(new Set());
  const [unselectedMembers, setUnselectedMembers] = useState<Set<string>>();
  const totalCount = searchCrowdQuery.data?.total;
  const allSelected =
    selectedMembers?.size === totalCount || unselectedMembers?.size === 0;
  const mapRef = useRef<MapRef>(null);
  const [
    sendPushNotificationFormDialogOpened,
    setSendPushNotificationFormDialogOpened,
  ] = useState(false);
  const [addBadgesFormDialogOpened, setAddBadgesFormDialogOpened] =
    useState(false);
  const [removeBadgesFormDialogOpened, setRemoveBadgesFormDialogOpened] =
    useState(false);

  const selectedCount =
    selectedMembers?.size ??
    (totalCount !== undefined && !!unselectedMembers
      ? totalCount - unselectedMembers?.size
      : undefined);
  const isEmpty = !searchCrowdQuery.isLoading && !totalCount;
  const hasGeoFilter = !!(
    typeof filters.lat === 'number' &&
    typeof filters.lng === 'number' &&
    (filters.radius ?? 0) > 0
  );
  const canSearchText = !hasGeoFilter;
  const filtered =
    hasGeoFilter ||
    !!filters.search ||
    !!filters.articleId ||
    !!filters.onlyBadges ||
    !!filters.badgeId ||
    !!filters.type;

  const centerMap = useCallback((geolocation: IGeolocation): void => {
    if (!mapRef.current) {
      return;
    }

    mapRef.current.flyTo({
      center: [geolocation.longitude, geolocation.latitude],
      zoom: 15,
      duration: 300,
    });
  }, []);

  const getNearFilter = useCallback(():
    | Partial<ISearchCommunityFilters>
    | undefined => {
    const map = mapRef.current;
    if (!map) {
      return undefined;
    }

    const center = map.getCenter();
    const bounds = map.getBounds();
    const east = bounds?.getEast();
    const radiusInMeters = east
      ? distance([center.lng, center.lat], [east, center.lat], {
          units: 'meters',
        })
      : undefined;
    if (!radiusInMeters) {
      return {};
    }

    return {
      lat: center.lat,
      lng: center.lng,
      radius: Math.round(radiusInMeters),
    };
  }, []);

  const handleSearchFormSubmit: SubmitHandler<ISearchCommunityFormBodyValues> =
    useCallback(
      (values) => {
        setSelectedMembers(new Set());
        setUnselectedMembers(undefined);
        setFilters((filters) => ({
          ...filters,
          search: values.search,
          categoryId: values.categoryId,
          articleId: values.articleId,
          onlyBadges: values.onlyBadges,
          badgeId: values.badgeId,
          type: values.type,
          ...(values.near
            ? getNearFilter()
            : {
                lat: undefined,
                lng: undefined,
                radius: undefined,
              }),
        }));
        pagination.updatePage(0);
      },
      [pagination, getNearFilter],
    );

  const filtersForm = useForm<ISearchCommunityFormBodyValues>({
    defaultValues: {
      search: searchParams.get('search') ?? '',
      categoryId: filters.categoryId,
      articleId: filters.articleId,
      onlyBadges: filters.onlyBadges,
      badgeId: filters.badgeId,
      near: !!(
        typeof filters.lat === 'number' &&
        typeof filters.lng === 'number' &&
        (filters.radius ?? 0) > 0
      ),
      type: filters.type,
    },
  });

  const handleSearchFormReset = useCallback((): void => {
    setFilters({
      search: undefined,
      categoryId: undefined,
      articleId: undefined,
      onlyBadges: undefined,
      badgeId: undefined,
      sortBy: undefined,
      sortOrder: undefined,
      type: 'usersAndDevices',
      lat: undefined,
      lng: undefined,
      radius: undefined,
    });
    filtersForm.setValue('search', '');
    filtersForm.setValue('categoryId', '');
    filtersForm.setValue('articleId', '');
    filtersForm.setValue('onlyBadges', '');
    filtersForm.setValue('badgeId', '');
    filtersForm.setValue('near', false);
    filtersForm.setValue('type', 'usersAndDevices');
  }, [filtersForm]);

  const handleSelectChange = useCallback(
    (member: IApiCrowdSearchItemResponseBody, selected: boolean): void => {
      if (selectedMembers) {
        setSelectedMembers(
          (selectedMembers) =>
            new Set(
              selected
                ? [...(selectedMembers ?? []), member._id]
                : [...(selectedMembers ?? [])].filter(
                    (id) => id !== member._id,
                  ),
            ),
        );
      }

      if (unselectedMembers) {
        setUnselectedMembers(
          (unselectedMembers) =>
            new Set(
              selected
                ? [...(unselectedMembers ?? [])].filter(
                    (id) => id !== member._id,
                  )
                : [...(unselectedMembers ?? []), member._id],
            ),
        );
      }
    },
    [selectedMembers, unselectedMembers],
  );

  const handleSortChange = useCallback(
    (sortBy: string, sortOrder: string): void => {
      setFilters((filter) => ({
        ...filter,
        sortBy: sortBy as IApiCrowdSearchRequestBody['sortBy'],
        sortOrder: sortOrder as IApiCrowdSearchRequestBody['sortOrder'],
      }));
      pagination.updatePage(0);
    },
    [pagination],
  );

  const handleSearchWithin = useCallback((): Promise<void> => {
    filtersForm.setValue('near', true);
    setFilters((filter) => ({
      ...filter,
      ...getNearFilter(),
    }));

    return filtersForm.handleSubmit(handleSearchFormSubmit)();
  }, [filtersForm, getNearFilter, handleSearchFormSubmit]);

  const selectionFilters = useMemo(
    () =>
      allSelected
        ? filters
        : unselectedMembers
          ? {
              ...filters,
              notMemberId: [...unselectedMembers],
            }
          : selectedMembers
            ? {
                memberId: [...selectedMembers],
              }
            : {},
    [allSelected, filters, selectedMembers, unselectedMembers],
  );

  const handleSendPushNotification: SubmitHandler<ISendPushNotificationFormValues> =
    useCallback(
      (values) =>
        notifyCrowdMutation.mutateAsync({
          appId,
          body: {
            filters: {
              ...selectionFilters,
              type: unserializeType(filters.type),
            },
            payload: {
              title: values.title!,
              content: values.content!,
              extraData:
                values.target && values.target !== 'feed'
                  ? {
                      articleId:
                        values.target === 'article'
                          ? values.targetValue
                          : undefined,
                      userArticleId:
                        values.target === 'userArticle'
                          ? values.targetValue
                          : undefined,
                      chatRoomId:
                        values.target === 'chatRoom'
                          ? values.targetValue
                          : undefined,
                      webviewUrl:
                        values.target === 'url'
                          ? values.targetValue
                          : undefined,
                      hideNavbar:
                        values.target === 'url' ? values.hideNavbar : undefined,
                    }
                  : undefined,
            },
          },
        }),
      [appId, selectionFilters, notifyCrowdMutation, filters.type],
    );

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries(searchCrowdQueryKeyFactory({ appId }));
    await searchCrowdQuery.refetch();
  }, [searchCrowdQuery, queryClient, appId]);

  const handleAddBadges: SubmitHandler<IBadgesFormValues> = useCallback(
    (values) =>
      addBadgesCrowdMutation.mutateAsync(
        {
          appId,
          body: {
            filters: {
              ...selectionFilters,
              type: unserializeType(filters.type),
            },
            badgeIds: values.badgeIds!,
          },
        },
        { onSuccess: refresh },
      ),
    [selectionFilters, addBadgesCrowdMutation, refresh, appId, filters.type],
  );

  const handleRemoveBadges: SubmitHandler<IBadgesFormValues> = useCallback(
    (values) =>
      removeBadgesCrowdMutation.mutateAsync(
        {
          appId,
          body: {
            filters: {
              ...selectionFilters,
              type: unserializeType(filters.type),
            },
            badgeIds: values.badgeIds!,
          },
        },
        { onSuccess: refresh },
      ),
    [selectionFilters, removeBadgesCrowdMutation, refresh, appId, filters.type],
  );

  return (
    <>
      <SendPushNotificationFormDialog
        defaultValues={{
          target: 'feed',
        }}
        opened={sendPushNotificationFormDialogOpened}
        onClose={() => setSendPushNotificationFormDialogOpened(false)}
        estimatedReachCount={selectedCount}
        onSubmit={handleSendPushNotification}
      />

      <BadgesFormDialog
        opened={addBadgesFormDialogOpened}
        onClose={() => setAddBadgesFormDialogOpened(false)}
        appId={appId}
        onSubmit={handleAddBadges}
        submitButtonLabel={t('common.add')}
        headline={t('SearchCommunity.dialogs.addBadges.title')}
        messageOnSuccess={t('SearchCommunity.dialogs.addBadges.success')}
      />

      <BadgesFormDialog
        opened={removeBadgesFormDialogOpened}
        onClose={() => setRemoveBadgesFormDialogOpened(false)}
        appId={appId}
        onSubmit={handleRemoveBadges}
        submitButtonLabel={t('common.remove')}
        headline={t('SearchCommunity.dialogs.removeBadges.title')}
        messageOnSuccess={t('SearchCommunity.dialogs.removeBadges.success')}
      />

      <Stack gap={4}>
        {MAP_ENABLED && (
          <SearchCommunityMap
            sx={styles.map}
            appId={appId}
            filter={{
              ...filters,
              type: unserializeType(filters.type),
            }}
            onSearchWithin={handleSearchWithin}
            ref={mapRef}
          />
        )}

        <Stack gap={2}>
          <Stack gap={4}>
            {filtered && (
              <Stack horizontal gap={2}>
                {typeof filters.lat === 'number' &&
                  typeof filters.lng === 'number' &&
                  (filters.radius ?? 0) > 0 && (
                    <InputChip
                      icon={<FontAwesomeIcon icon={faLocationDot} />}
                      label={t('SearchCommunity.filters.near', {
                        value: `${Number(filters.lat).toFixed(2)}, ${Number(filters.lng).toFixed(2)} (${Math.round((filters.radius ?? 0) / 1000)} km)`,
                      })}
                      onDelete={() => {
                        setFilters((filter) => ({
                          ...filter,
                          lat: undefined,
                          lng: undefined,
                          radius: undefined,
                        }));
                        filtersForm.setValue('near', false);
                      }}
                    />
                  )}

                {canSearchText && filters.search && (
                  <InputChip
                    label={t('SearchCommunity.filters.search', {
                      value: filters.search,
                    })}
                    onDelete={() => {
                      setFilters((filter) => ({
                        ...filter,
                        search: undefined,
                      }));
                      filtersForm.setValue('search', '');
                    }}
                  />
                )}

                {filters.articleId && (
                  <InputChip
                    label={t('SearchCommunity.filters.article', {
                      value: filters.articleId,
                    })}
                    onDelete={() => {
                      setFilters((filter) => ({
                        ...filter,
                        articleId: undefined,
                      }));
                      filtersForm.setValue('articleId', '');
                    }}
                  />
                )}

                {filters.onlyBadges && (
                  <InputChip
                    label={t('SearchCommunity.filters.onlyBadges', {
                      value: t(
                        `SearchCommunityFormMainSection.fields.badges.options.${filters.onlyBadges}`,
                      ),
                    })}
                    onDelete={() => {
                      setFilters((filter) => ({
                        ...filter,
                        onlyBadges: undefined,
                      }));
                      filtersForm.setValue('onlyBadges', '');
                    }}
                  />
                )}

                {filters.badgeId && (
                  <InputChip
                    label={t('SearchCommunity.filters.badge', {
                      value: filters.badgeId,
                    })}
                    onDelete={() => {
                      setFilters((filter) => ({
                        ...filter,
                        badgeId: undefined,
                      }));
                      filtersForm.setValue('badgeId', '');
                    }}
                  />
                )}
              </Stack>
            )}

            <div>
              <Skeleton loaded={!searchCrowdQuery.isFetching} length={12}>
                {searchCrowdQuery.data?.total !== undefined ? (
                  <Text variant="label" size="lg">
                    {t('SearchCommunity.resultsCount', {
                      count: searchCrowdQuery.data.total,
                    })}
                  </Text>
                ) : (
                  'n/a'
                )}
              </Skeleton>

              <Stack horizontal gap={2}>
                <div>
                  {t('SearchCommunity.batchActions', {
                    count: selectedCount ?? 0,
                  })}
                </div>
                <Button
                  variant="outlined"
                  icon={<FontAwesomeIcon icon={faBell} />}
                  disabled={!selectedCount}
                  onClick={() => setSendPushNotificationFormDialogOpened(true)}
                >
                  {t('SearchCommunity.sendPushNotification')}
                </Button>
                <Button
                  variant="outlined"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  disabled={!selectedCount}
                  onClick={() => setAddBadgesFormDialogOpened(true)}
                >
                  {t('SearchCommunity.addBadges')}
                </Button>
                <Button
                  variant="outlined"
                  icon={<FontAwesomeIcon icon={faMinus} />}
                  disabled={!selectedCount}
                  onClick={() => setRemoveBadgesFormDialogOpened(true)}
                >
                  {t('SearchCommunity.removeBadges')}
                </Button>
              </Stack>
            </div>
          </Stack>

          <ContentBlock sx={sx}>
            {searchCrowdQuery.isError ? (
              <ContentBlock.Body first>
                <ErrorMessage error={searchCrowdQuery.error} />
              </ContentBlock.Body>
            ) : isEmpty ? (
              filtered ? (
                <ContentBlock.Empty
                  title={t('SearchCommunity.noResult.title')}
                  subtitle={t('SearchCommunity.noResult.subtitle')}
                />
              ) : (
                <ContentBlock.Empty
                  title={t('SearchCommunity.empty.title')}
                  subtitle={t('SearchCommunity.empty.subtitle')}
                />
              )
            ) : (
              <>
                <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
                  <Table.Row first>
                    <Table.Header>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={
                          !!selectedMembers?.size || !!unselectedMembers?.size
                        }
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedMembers(undefined);
                            setUnselectedMembers(new Set());
                          } else {
                            setSelectedMembers(new Set());
                            setUnselectedMembers(undefined);
                          }
                        }}
                        disabled={searchCrowdQuery.isLoading}
                      />
                    </Table.Header>
                    <Table.Header>
                      {selectedCount
                        ? t('SearchCommunity.selectedCount', {
                            count: selectedCount,
                          })
                        : null}
                    </Table.Header>
                    <Table.Header />
                    <Table.Header>
                      {t('SearchCommunity.headers.badges')}
                    </Table.Header>
                    <Table.SortableHeader
                      sortBy="readingTime"
                      sortOrder={
                        filters.sortBy === 'readingTime'
                          ? filters.sortOrder
                          : undefined
                      }
                      onChange={handleSortChange}
                    >
                      {t('SearchCommunity.headers.readingTime')}
                    </Table.SortableHeader>
                    <Table.SortableHeader
                      sortBy="firstMetricAt"
                      sortOrder={
                        filters.sortBy === 'firstMetricAt'
                          ? filters.sortOrder
                          : undefined
                      }
                      onChange={handleSortChange}
                      reverseIcon
                    >
                      {t('SearchCommunity.headers.firstMetricAt')}
                    </Table.SortableHeader>
                    <Table.SortableHeader
                      sortBy="lastMetricAt"
                      sortOrder={
                        filters.sortBy === 'lastMetricAt'
                          ? filters.sortOrder
                          : undefined
                      }
                      onChange={handleSortChange}
                      reverseIcon
                    >
                      {t('SearchCommunity.headers.lastMetricAt')}
                    </Table.SortableHeader>
                  </Table.Row>

                  {searchCrowdQuery.isLoading || searchCrowdQuery.isPreviousData
                    ? createSequence(
                        searchCrowdQuery.data?.items?.length ??
                          DEFAULT_SKELETON_COUNT,
                      ).map((index) => (
                        <SearchCommunityListItemSkeleton key={index} />
                      ))
                    : searchCrowdQuery.data?.items?.map((member) => (
                        <SearchCommunityListItem
                          key={member._id}
                          member={member}
                          selected={
                            selectedMembers?.has(member._id) ||
                            (!!unselectedMembers &&
                              !unselectedMembers.has(member._id))
                          }
                          onSelectChange={(selected) =>
                            handleSelectChange(member, selected)
                          }
                          onUpdate={refresh}
                          onGeolocate={centerMap}
                        />
                      ))}
                </Table>

                <ContentBlock.Footer>
                  <Pagination pagination={pagination} total={totalCount} />
                </ContentBlock.Footer>
              </>
            )}
          </ContentBlock>
        </Stack>
      </Stack>

      <SearchCommunityAside
        appId={appId}
        form={filtersForm}
        onSubmit={handleSearchFormSubmit}
        onReset={handleSearchFormReset}
        loading={searchCrowdQuery.isFetching}
      />
    </>
  );
};
